// @spec SHL-STR-051
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Activity,
  Building2,
  CheckCircle2,
  Gauge,
  ShieldAlert,
  TrendingUp,
  Users,
  XCircle,
} from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Card, CardContent } from '@/components/ui/card';
import {
  DashboardActivityChart,
  DashboardAlerts,
  DashboardQuickActions,
  DashboardStatCard,
  useAdminDashboard,
} from '@/features/admin';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_authenticated/admin/')({
  component: AdminDashboardPage,
});

/**
 * Check if user has Super Admin role from Clerk metadata.
 */
function isSuperAdmin(user: ReturnType<typeof useUser>['user']): boolean {
  if (!user) return false;

  const publicMetadata = user.publicMetadata as Record<string, unknown> | undefined;
  return publicMetadata?.role === 'super_admin';
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl">
        <div className="animate-pulse space-y-6">
          {/* Header skeleton */}
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-lg bg-muted" />
            <div className="space-y-2">
              <div className="h-6 w-48 rounded bg-muted" />
              <div className="h-4 w-32 rounded bg-muted" />
            </div>
          </div>

          {/* Stat cards skeleton */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-32 rounded-xl bg-muted" />
            ))}
          </div>

          {/* Chart and alerts skeleton */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="h-80 rounded-xl bg-muted" />
            <div className="h-80 rounded-xl bg-muted" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SystemHealthIndicator({ status }: { status: string }) {
  const config = {
    healthy: {
      icon: CheckCircle2,
      label: 'All Systems Operational',
      color: 'text-green-600 dark:text-green-400',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
    },
    degraded: {
      icon: Activity,
      label: 'Some Systems Degraded',
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
    },
    unhealthy: {
      icon: XCircle,
      label: 'System Issues Detected',
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
    },
  };

  const statusKey = status as keyof typeof config;
  const statusConfig = statusKey in config ? config[statusKey] : config.healthy;
  const Icon = statusConfig.icon;

  return (
    <Card>
      <CardContent className="flex items-center gap-3 p-4">
        <div className={cn('rounded-lg p-2', statusConfig.bgColor)}>
          <Icon className={cn('h-5 w-5', statusConfig.color)} aria-hidden="true" />
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{statusConfig.label}</p>
          <p className="text-xs text-muted-foreground">System Health Status</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AdminDashboardPage() {
  const { user, isLoaded: userLoaded } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const hasAccess = isSuperAdmin(user);
  const { data, isLoading, error } = useAdminDashboard();

  // Show loading while checking user
  if (!userLoaded) {
    return <LoadingSkeleton />;
  }

  // Show 403 if not Super Admin
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-lg text-center">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-destructive/10 p-4">
              <ShieldAlert className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <h1 className="mb-2 text-2xl font-semibold text-foreground">{t('accessDenied.title')}</h1>
          <p className="mb-6 text-muted-foreground">
            {t('accessDenied.superAdminRequired')}
          </p>
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => {
              void navigate({ to: '/dashboard' });
            }}
          >
            {t('accessDenied.returnToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  // Show loading skeleton while fetching data
  if (isLoading) {
    return <LoadingSkeleton />;
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">Failed to load dashboard data. Please try again.</p>
          </div>
        </div>
      </div>
    );
  }

  const metrics = data?.metrics;

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Gauge className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">Platform overview and system health</p>
            </div>
          </div>
        </div>

        {/* System health banner */}
        {data?.systemHealth && <SystemHealthIndicator status={data.systemHealth.status} />}

        {/* Stat cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <DashboardStatCard
            title="Total Organizations"
            value={metrics?.totalOrganizations ?? 0}
            subtitle={`${String(metrics?.activeOrganizations ?? 0)} active`}
            icon={Building2}
            variant="default"
          />
          <DashboardStatCard
            title="Total Users"
            value={metrics?.totalUsers ?? 0}
            subtitle={`${String(metrics?.activeUsers ?? 0)} active (30d)`}
            icon={Users}
            variant="default"
          />
          <DashboardStatCard
            title="New Users"
            value={metrics?.newUsersThisMonth ?? 0}
            subtitle="This month"
            icon={TrendingUp}
            variant="success"
          />
          <DashboardStatCard
            title="New Organizations"
            value={metrics?.newOrgsThisMonth ?? 0}
            subtitle="This month"
            icon={Activity}
            variant="success"
          />
        </div>

        {/* Activity chart and alerts */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <DashboardActivityChart data={data?.recentActivity ?? []} />
          <DashboardAlerts alerts={data?.alerts ?? []} />
        </div>

        {/* Quick actions */}
        <DashboardQuickActions />
      </div>
    </div>
  );
}
